import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { WA_AUDIT_NAV_ITEM, visibleWaNavItems } from '../layout/waNavItems';
import { WaCornerLogo, WaFooter, WaTopBar } from '../layout/WaChrome';

const formatTime = (value) => (value ? new Date(value).toLocaleString() : 'N/A');
const formatStatus = (value) => (value ? 'Active' : 'Inactive');
const orNA = (value) => (value === null || value === undefined || value === '' ? 'N/A' : value);
const asList = (object) => (Array.isArray(object) ? object : []);

// Every field of a business portfolio, and of the client it belongs to, as [label, value] pairs - the page shows them as "label : value",
// the portfolio's in the left column and the client's in the right.
const portfolioFields = (portfolio) => [
  ['Portfolio Id', orNA(portfolio.id)],
  ['Business Portfolio Id', orNA(portfolio.clientBusinessPortfolioId)],
  ['Portfolio Status', formatStatus(portfolio.status)],
  ['Portfolio Created At', formatTime(portfolio.createdTimestamp)],
  ['Portfolio Updated At', formatTime(portfolio.updatedTimestamp)],
];
const clientFields = (portfolio) => {
  const client = portfolio.waClientDetail || {};
  return [
    ['Client Record Id', orNA(client.id)],
    ['Client Id', orNA(client.clientId)],
    ['Client Name', orNA(client.clientName)],
    ['Client Description', orNA(client.clientDescription)],
    ['Client Status', formatStatus(client.status)],
    ['Client Created At', formatTime(client.createdTimestamp)],
    ['Client Updated At', formatTime(client.updatedTimestamp)],
  ];
};

const FieldRows = ({ fields }) => fields.map(([label, value]) => (
  <div className="wa-dash-row" key={label}>
    <span className="wa-dash-label">{label}</span>
    <span className="wa-dash-sep">:</span>
    <span className="wa-dash-value">{value}</span>
  </div>
));

// Active ones first: a switched-off Meta app or portfolio is not "the current" one. When none is active, whatever there is.
const activeFirst = (list) => {
  const active = list.filter((item) => item.status);
  return active.length > 0 ? active : list;
};

/**
 * The WhatsApp dashboard, where a signed-in user lands (/wadashboard): the Meta app and the business portfolio of the current tenant, and
 * below them one tile for every screen of the WhatsApp side panel, three to a row. A tile opens that screen as the side panel's link would.
 * Read only. The tenant is the one the sign-in token names, so the service sends only this tenant's rows, and only those of the WABAs the
 * signed-in user may see.
 */
const WaDashboardPage = () => {
  const waApi = useWaApi();
  const navigate = useNavigate();
  const access = useUserProfileAccess();
  const { accountActive, canViewPiiAuditLog, loaded } = access;
  const locked = loaded && !accountActive;

  const [appNames, setAppNames] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    // An account that is not active yet gets none of this (the service would refuse it anyway)
    if (!loaded || locked) {
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [apps, portfolioList] = await Promise.all([
          waApi.get(process.env.REACT_APP_GET_ALL_WA_META_APP_DETAILS_ENDPOINT),
          waApi.get(process.env.REACT_APP_GET_ALL_WA_CLIENT_PORTFOLIO_DETAILS_ENDPOINT),
        ]);
        if (cancelled) return;
        const problems = [];
        if (apps.data.httpStatus === 200) {
          setAppNames(activeFirst(asList(apps.data.object)).map((app) => app.waAppName).filter(Boolean));
        } else {
          problems.push(apps.data.message || 'Unable to load the App.');
        }
        if (portfolioList.data.httpStatus === 200) {
          setPortfolios(activeFirst(asList(portfolioList.data.object)));
        } else {
          problems.push(portfolioList.data.message || 'Unable to load the business portfolio.');
        }
        setError(problems.length > 0 ? problems.join(' ') : null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Unable to load the App and business portfolio.');
        }
      } finally {
        if (!cancelled) setDataLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [waApi, loaded, locked]);

  const navItems = visibleWaNavItems(access);
  const tiles = canViewPiiAuditLog ? [...navItems, WA_AUDIT_NAV_ITEM] : navItems;

  return (
    <div className="wa-dashboard-page">
      <WaCornerLogo />
      <div className="wa-dashboard-inner">
        <WaTopBar locked={locked} />

        <main className="wa-dashboard-main">
          {locked ? (
            <section className="wa-dash-card">
              <h1 className="wa-dash-heading">Account Pending Activation</h1>
              <p>
                Your profile is not active yet, so the rest of the application is unavailable. Use My User Profile to review
                your details, and ask an administrator to activate your account.
              </p>
            </section>
          ) : (
            <>
              {/* First half: the Meta app and the business portfolio of the current tenant, with - as a separate panel beside it - the way to
                  the privacy policy. That panel is only on this page. It links to the public page of the UI's public/ folder (no login),
                  so a plain link, opened in a new tab. */}
              <div className="wa-dash-top">
                <section className="wa-dash-card" aria-label="App and business portfolio">
                  <div className="wa-dash-row wa-dash-appname">
                    <span className="wa-dash-label">App Name</span>
                    <span className="wa-dash-sep">:</span>
                    <span className="wa-dash-value">{appNames.length > 0 ? appNames.join(', ') : (dataLoaded ? 'N/A' : '...')}</span>
                  </div>

                  {/* Two lines on what the platform is, just under the app name */}
                  <p className="wa-dash-intro">
                    <span>Primedha Connect is a multi-tenant messaging platform for sending template notifications and other text, docs etc to your customers over the Messaging Business Platform.</span>
                    <span>"As a Technology Provider, Primedha Technologies enables businesses to engage their customers on messaging channels."</span>
                  </p>

                  <h2 className="wa-dash-heading">Business Portfolio</h2>
                  {error && <div role="alert" className="wa-dash-alert">{error}</div>}
                  {dataLoaded && !error && portfolios.length === 0 && (
                    <p className="wa-dash-empty">No business portfolio is set up for this tenant yet.</p>
                  )}
                  {portfolios.map((portfolio) => (
                    <div className="wa-dash-portfolio" key={portfolio.id ?? portfolio.clientBusinessPortfolioId}>
                      <div className="wa-dash-column"><FieldRows fields={portfolioFields(portfolio)} /></div>
                      <div className="wa-dash-column"><FieldRows fields={clientFields(portfolio)} /></div>
                    </div>
                  ))}
                </section>

                <aside className="wa-dash-legal" aria-label="Privacy policy and terms of service">
                  <span className="wa-dash-legal-label">Privacy Policy &amp; Terms of Service</span>
                  <span className="wa-dash-legal-info">
                    How Primedha Connect handles personal data, its terms of use, security and how to ask for data deletion.
                  </span>
                  <a href={`${process.env.PUBLIC_URL || ''}/wa-privacy-policy.html`} target="_blank" rel="noopener noreferrer">
                    Open the Privacy Policy &amp; Terms &#8599;
                  </a>
                </aside>
              </div>

              {/* Second half: a tile for every screen of the side panel, three to a row */}
              <section className="wa-dash-tiles-section" aria-label="WhatsApp screens">
                <div className="wa-tile-grid">
                  {tiles.map((item) => (
                    <button type="button" className="wa-tile" key={item.path} onClick={() => navigate(item.path)}>
                      <span className="wa-tile-icon"><FontAwesomeIcon icon={item.icon} /></span>
                      <span className="wa-tile-label">{item.label}</span>
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}
        </main>

        <WaFooter />
      </div>
    </div>
  );
};

export default WaDashboardPage;
